import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  rbacAssignmentSchema,
  type RbacAssignment,
  type SubjectType,
} from "@/features/rbac/schemas/rbac";
import { listRoles } from "@/features/rbac/services/rolesService";
import {
  assertNotSelfAssignment,
  assertRoleManagementSurvives,
} from "@/features/rbac/utils/policy";

export const RBAC_ASSIGNMENTS_COLLECTION = "rbac_assignments";

function getAssignmentsCollection(): CollectionReference<DocumentData> {
  return collection(db, RBAC_ASSIGNMENTS_COLLECTION);
}

function getAssignmentDoc(subjectUid: string): DocumentReference<DocumentData> {
  return doc(db, RBAC_ASSIGNMENTS_COLLECTION, subjectUid);
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value) {
    const candidate = (value as { toDate: () => Date }).toDate();
    return candidate instanceof Date ? candidate : null;
  }
  return null;
}

function mapAssignment(uid: string, data: DocumentData): RbacAssignment | null {
  const parsed = rbacAssignmentSchema.safeParse({
    subjectUid: uid,
    subjectType: data.subject_type,
    subjectEmail: typeof data.subject_email === "string" ? data.subject_email : null,
    roleIds: Array.isArray(data.role_ids) ? data.role_ids : [],
    updatedAt: toDate(data.updated_at),
    updatedBy: typeof data.updated_by === "string" ? data.updated_by : null,
  });

  return parsed.success ? parsed.data : null;
}

/** Asignación de una persona, o `null` si todavía resuelve por el camino heredado. */
export async function getAssignment(subjectUid: string): Promise<RbacAssignment | null> {
  if (!subjectUid) return null;

  const snapshot = await getDoc(getAssignmentDoc(subjectUid));
  if (!snapshot.exists()) return null;

  return mapAssignment(snapshot.id, snapshot.data());
}

/** Todas las asignaciones explícitas. Se usa en la pantalla de Roles. */
export async function listAssignments(): Promise<RbacAssignment[]> {
  const snapshot = await getDocs(getAssignmentsCollection());

  return snapshot.docs
    .map((docSnap) => mapAssignment(docSnap.id, docSnap.data()))
    .filter((assignment): assignment is RbacAssignment => assignment !== null);
}

export interface SetAssignmentInput {
  subjectUid: string;
  subjectType: SubjectType;
  subjectEmail: string | null;
  roleIds: readonly string[];
}

/**
 * Escribe los roles de una persona.
 *
 * Antes de escribir comprueba dos cosas, en el servicio y no solo en la UI:
 * que nadie edite sus propios roles, y que el cambio no deje a la plataforma
 * sin nadie capaz de administrar roles.
 *
 * Una lista vacía borra la asignación, con lo que la persona vuelve a resolver
 * por el camino heredado en vez de quedarse sin ningún permiso.
 */
export async function setAssignment(
  input: SetAssignmentInput,
  actorUid: string | null
): Promise<void> {
  assertNotSelfAssignment(actorUid, input.subjectUid);

  const [roles, assignments] = await Promise.all([listRoles(), listAssignments()]);

  const roleIds = [...new Set(input.roleIds.filter(Boolean))].sort();
  const surviving = assignments
    .filter((assignment) => assignment.subjectUid !== input.subjectUid)
    .map((assignment) => ({ roleIds: assignment.roleIds }));

  if (roleIds.length > 0) surviving.push({ roleIds });

  assertRoleManagementSurvives(surviving, roles);

  if (roleIds.length === 0) {
    await deleteDoc(getAssignmentDoc(input.subjectUid));
    return;
  }

  await setDoc(getAssignmentDoc(input.subjectUid), {
    subject_uid: input.subjectUid,
    subject_type: input.subjectType,
    subject_email: input.subjectEmail,
    role_ids: roleIds,
    updated_at: new Date(),
    updated_by: actorUid,
  });
}
